// lib/operateur-automates.js — LA TABLE DE VÉRITÉ DES AUTOMATES.
//
// ══ POURQUOI CE MODULE EXISTE ═══════════════════════════════════════════════
//
// L'opérateur s'efface devant « le système s'en charge tout seul » : il ne
// propose pas de geste quand un automate agit déjà. C'est juste — À CONDITION
// QUE L'AUTOMATE EXISTE. Le 2026-09-13, un paiement refusé n'avait aucun
// geste « parce que la relance automatique existe » ; elle n'existe pas
// (`event_payment_ko` n'est qu'un libellé dans une contrainte SQL, jamais
// implémenté dans `lib/messages-engine.js`). Un silence à deux étages :
// l'automate ne fait rien, et l'opérateur s'efface devant un automate fantôme.
// Rien à l'écran ne peut révéler ça.
//
// Ce module est donc une LISTE FERMÉE : les seuls automates que l'opérateur a
// le droit d'invoquer pour ne PAS proposer un geste. Chaque entrée nomme le
// fichier et la fonction exportée qui la prouvent, et son déclencheur réel —
// et le test `src/lib/operateur-automates.test.js` REQUIERT ces fichiers,
// VÉRIFIE que la fonction existe, et COMPARE le rythme déclaré au rythme
// écrit dans `vercel.json` / `.github/workflows/*.yml` / la migration. Une
// entrée dont la preuve manque fait rougir le test ; une doc qui diverge du
// fichier aussi.
//
// Un automate qui n'existe pas s'écrit EXPLICITEMENT `automate: null` avec
// sa raison. Un `null` documenté vaut mieux qu'une absence : c'est lui que
// l'opérateur lira pour PROPOSER un geste au lieu de s'effacer.
//
// ══ CE QUE CE MODULE NE FAIT PAS ════════════════════════════════════════════
//
// · Il ne déclenche rien, n'importe aucun automate, ne lit aucune table.
//   Ce sont des CHAÎNES : le test seul fait les `require`.
// · Il ne corrige pas les sources d'événements (`lib/operateur-evenements.js`)
//   — le raccordement viendra après, quand elles pourront le consulter.
// · Il ne dit pas qu'un automate a TOURNÉ aujourd'hui (ça, c'est
//   `lib/operateur-relances.js`, le second temps) — il dit qu'il EXISTE et
//   qu'il est PLANIFIÉ.
//
// ══ LES DÉCLENCHEURS, ET COMMENT LE TEST LES VÉRIFIE ═══════════════════════
//
//   · `vercel`      : `chemin` doit figurer dans `vercel.json › crons`, et
//                     `rythmes` doit être EXACTEMENT la liste des `schedule`
//                     trouvés pour ce chemin (un chemin peut avoir 2 ticks).
//   · `workflow`    : `fichier` doit exister dans `.github/workflows/`, avoir
//                     un bloc `schedule:` NON commenté, et `rythmes` doit être
//                     exactement la liste des `- cron:` non commentés.
//                     Un workflow réduit à `workflow_dispatch` (comme
//                     `messages-engine.yml` depuis ADR-271) ne PLANIFIE rien.
//   · `trigger_sql` : la `migration` doit exister et contenir un
//                     `CREATE TRIGGER <nom>` non commenté. ⚠️ Le test lit le
//                     FICHIER, pas la base : l'apply en prod se vérifie sur
//                     `pg_trigger`, jamais sur ce test.
//   · `webhook`     : pas de rythme — la preuve est la fonction seule.
//
// Tous les rythmes cron sont en UTC (Vercel comme GitHub Actions).

/**
 * @typedef {{ fichier: string, fonction: string }} Preuve
 *   `fonction` peut être un chemin pointé (`_test.releaseZombieCheckoutBlocks`).
 * @typedef {{ type: "vercel", chemin: string, rythmes: string[] }
 *         | { type: "workflow", fichier: string, rythmes: string[] }
 *         | { type: "trigger_sql", migration: string, trigger: string }
 *         | { type: "webhook", via: string }} Declencheur
 * @typedef {{ preuves: Preuve[], declencheurs: Declencheur[], phrase: string, reserve?: string }} Automate
 * @typedef {{ cle: string, automate: Automate | null, raison?: string }} Entree
 */

/** @type {Entree[]} */
const AUTOMATES = [
  {
    // ⚠️ EN SOMMEIL DEPUIS LE 30/10/2026 — Beds24 en sommeil (lot L2,
    // P-3.37). Même forme que `annonces_nouvelles` : un `null` documenté,
    // pour que l'opérateur PROPOSE au lieu de s'effacer devant un automate
    // qui ne tourne plus. Rien n'est supprimé : le code est en place.
    cle: "annulation_rouvre",
    automate: null,
    raison:
      "Beds24 est en sommeil depuis le 30/10 (décision Dimitri, lot L2 de P-3.37 ; interrupteur unique lib/beds24-sommeil.js) : plus aucun appel à Beds24, et la tâche planifiée est retirée de vercel.json. Une annulation reçue de Beds24 ne rouvre donc plus les dates toute seule (le webhook ne peut plus relire la réservation, la synchro et le filet de 6 h ne tournent plus). L'opérateur doit le dire, pas promettre une réouverture. Au réveil : reprendre l'entrée d'avant (historique git du lot L2).",
  },
  {
    cle: "caution_relance_directe",
    automate: {
      preuves: [
        {
          fichier: "lib/direct-journey-sweep.js",
          fonction: "runCautionReminders",
        },
      ],
      declencheurs: [
        {
          type: "vercel",
          chemin: "/api/cron/direct-journey-daily",
          rythmes: ["0 8 * * *", "0 9 * * *"],
        },
        {
          type: "vercel",
          chemin: "/api/cron/direct-journey-evening",
          rythmes: ["0 * * * *"],
        },
      ],
      phrase:
        "La relance caution d'un séjour direct part toute seule : J-2 et J au sweep du matin (8 h UTC), J-1 au sweep du soir (20 h Paris).",
      reserve:
        "En MESSAGES_DRY_RUN le claim est posé mais l'envoi est sauté (ADR-272) — lire lib/operateur-relances.js pour savoir si elle est VRAIMENT partie.",
    },
  },
  {
    cle: "caution_relance_ota",
    automate: {
      preuves: [
        { fichier: "lib/messages-engine.js", fonction: "evaluateMessageSends" },
      ],
      declencheurs: [
        {
          type: "vercel",
          chemin: "/api/cron/beds24-sync?job=messages",
          rythmes: ["*/5 * * * *"],
        },
      ],
      phrase:
        "La relance caution d'une résa Booking part par le moteur de messages (déclencheur temporal_checkin_caution), évalué toutes les 5 min.",
      reserve:
        "Gaté MESSAGES_DRY_RUN : en simulation, une trace sent_dry_run est écrite et le voyageur ne reçoit rien.",
    },
  },
  {
    cle: "caution_liberation",
    automate: {
      preuves: [
        { fichier: "lib/release-cautions.js", fonction: "runReleaseCautions" },
      ],
      declencheurs: [
        // Vercel seul depuis le 02/10 (F7) : le `schedule` de
        // `stripe-release-cautions.yml` est retiré (workflow_dispatch conservé).
        {
          type: "vercel",
          chemin: "/api/cron/beds24-sync?job=release_cautions",
          rythmes: ["0 8 * * *"],
        },
      ],
      phrase:
        "La caution se libère toute seule 7 jours après le départ, au passage quotidien de 8 h UTC.",
      reserve:
        "En pratique, la banque du voyageur annule l'empreinte d'elle-même environ 7 jours après son autorisation, souvent avant ce passage : il ne libère que les empreintes encore vivantes.",
    },
  },
  {
    cle: "caution_alerte_expiration",
    automate: {
      preuves: [
        {
          fichier: "lib/caution-expiration-alerte.js",
          fonction: "runAlerteExpirationCautions",
        },
      ],
      declencheurs: [
        // Matin : après la libération J+7, dans le même passage (Vercel seul
        // depuis le 02/10, F7).
        {
          type: "vercel",
          chemin: "/api/cron/beds24-sync?job=release_cautions",
          rythmes: ["0 8 * * *"],
        },
        // Soir : alerte SEULE (décision Dimitri 01/10), jamais de libération.
        {
          type: "vercel",
          chemin: "/api/cron/beds24-sync?job=caution_alerte_expiration",
          rythmes: ["0 18 * * *"],
        },
      ],
      phrase:
        "Quand un incident est ouvert sur un séjour, une alerte prévient Dimitri avant que l'empreinte de caution expire (ou dit qu'il est trop tard), une seule fois par caution, à 8 h et 18 h UTC. Rien n'est encaissé automatiquement.",
      reserve:
        "L'alerte ne fait que prévenir : encaisser reste le geste de Dimitri (réservation › Paiements › Capturer la caution).",
    },
  },
  {
    // ⚠️ EN SOMMEIL DEPUIS LE 30/10/2026 — Beds24 en sommeil (lot L2,
    // P-3.37). Même forme que `annonces_nouvelles` : un `null` documenté,
    // pour que l'opérateur PROPOSE au lieu de s'effacer devant un automate
    // qui ne tourne plus. Rien n'est supprimé : le code est en place.
    cle: "pricing_drain",
    automate: null,
    raison:
      "Beds24 est en sommeil depuis le 30/10 (décision Dimitri, lot L2 de P-3.37 ; interrupteur unique lib/beds24-sommeil.js) : plus aucun appel à Beds24, et la tâche planifiée est retirée de vercel.json. Les prix en attente restent dans la file, intacts : rien ne les renvoie vers Beds24, ni le drain planifié ni le bouton de l'appli. L'opérateur doit le dire, pas promettre un renvoi. Au réveil : reprendre l'entrée d'avant (historique git du lot L2).",
  },
  {
    // ⚠️ N'EXISTE PAS — et ce `null` est la raison d'être de ce module.
    // Le garde a été vu ROUGIR le 2026-09-13 avec cette entrée déclarée
    // existante (`lib/messages-engine.js#relancerPaiementRefuse`, workflow
    // `messages-engine.yml`) : fonction absente, ET workflow sans `schedule:`
    // actif (réduit à `workflow_dispatch` depuis ADR-271). Le test
    // « un null documenté doit RESTER vrai » rougira le jour où quelqu'un
    // implémente `event_payment_ko` : on remplacera alors ce null par une
    // preuve, on ne le laissera pas mentir dans l'autre sens.
    cle: "paiement_relance",
    automate: null,
    raison:
      "Aucune relance automatique d'un paiement refusé n'existe : `event_payment_ko` n'est qu'un libellé dans une contrainte SQL (migrations 20260503000005/07), jamais implémenté dans lib/messages-engine.js, et lib/direct-journey-sweep.js n'a aucune passe sur `paiement_sejour_status = failed`. Le seul second temps est l'EXPIRATION (voir `paiement_expiration`). L'opérateur doit donc PROPOSER, pas s'effacer.",
  },
  {
    // ⚠️ EN PAUSE DEPUIS LE 01/10/2026 — abonnement au service d'annonces
    // résilié (24 € HT/mois). Interrupteur unique : src/lib/service-annonces.js.
    // Le relevé de nuit (vercel.json /api/scrape-prospects) tourne encore pour
    // les autres sources, mais n'interroge plus ce service, et son webhook
    // (api/melo-webhook.js) répond « en pause » sans rien écrire. L'opérateur
    // ne doit donc JAMAIS dire « de nouvelles annonces arrivent toutes seules ».
    cle: "annonces_nouvelles",
    automate: null,
    raison:
      "Service d'annonces en pause depuis le 01/10/2026 (abonnement résilié) : il apportait 4 078 annonces sur 4 106, les autres sources n'ont rien ajouté depuis juin. Plus aucune nouvelle annonce n'arrive seule dans « À démarcher » ; les annonces déjà récupérées restent consultables. L'opérateur doit le dire, pas promettre une collecte.",
  },
  {
    cle: "paiement_expiration",
    automate: {
      preuves: [
        {
          fichier: "api/cron/release-expired-bookings.js",
          fonction: "_test.releaseExpiredSignedBookings",
        },
        {
          fichier: "api/cron/release-expired-bookings.js",
          fonction: "_test._sendPaymentExpiredEmail",
        },
      ],
      declencheurs: [
        {
          type: "vercel",
          chemin: "/api/cron/release-expired-bookings",
          rythmes: ["0 6 * * *"],
        },
      ],
      phrase:
        "Un séjour direct signé mais jamais réglé expire tout seul 48 h après la signature (passage de 6 h UTC) : le voyageur en est prévenu et les dates se rouvrent.",
      reserve:
        "C'est une FIN, pas une relance : entre le refus et l'expiration, personne n'écrit au voyageur.",
    },
  },
  {
    cle: "signature_expiration",
    automate: {
      preuves: [
        {
          fichier: "lib/direct-journey-sweep.js",
          fonction: "runSignatureExpirySweep",
        },
      ],
      declencheurs: [
        {
          type: "vercel",
          chemin: "/api/cron/direct-journey-daily",
          rythmes: ["0 8 * * *", "0 9 * * *"],
        },
      ],
      phrase:
        "Un contrat non signé sous 72 h expire tout seul au sweep du matin (8 h UTC).",
      reserve: "Le mail d'expiration est gaté MESSAGES_DRY_RUN (ADR-272).",
    },
  },
  {
    cle: "demande_directe_auto_rejet",
    automate: {
      preuves: [
        {
          fichier: "api/cron/auto-reject-pending-requests.js",
          fonction: "_test.autoRejectExpiredPending",
        },
      ],
      declencheurs: [
        {
          type: "vercel",
          chemin: "/api/cron/auto-reject-pending-requests",
          rythmes: ["0 3 * * *"],
        },
      ],
      phrase:
        "Une demande directe restée sans réponse 48 h est rejetée toute seule au passage de 3 h UTC, et le voyageur en est prévenu.",
    },
  },
  {
    cle: "checkout_zombie_liberation",
    automate: {
      preuves: [
        {
          fichier: "api/cron/release-zombie-checkout-blocks.js",
          fonction: "_test.releaseZombieCheckoutBlocks",
        },
        {
          fichier: "lib/direct-journey-sweep.js",
          fonction: "runCommsOutboxSweep",
        },
      ],
      declencheurs: [
        {
          type: "vercel",
          chemin: "/api/cron/release-zombie-checkout-blocks",
          rythmes: ["*/15 * * * *"],
        },
      ],
      phrase:
        "Un paiement commencé puis abandonné libère ses dates tout seul dans le quart d'heure qui suit l'expiration du hold.",
    },
  },
  {
    cle: "menage_suit_sa_reservation",
    automate: {
      preuves: [],
      declencheurs: [
        {
          type: "trigger_sql",
          migration:
            "supabase/migrations/20261201000001_menage_suit_sa_reservation.sql",
          trigger: "menage_suit_sa_reservation",
        },
      ],
      phrase:
        "Quand une réservation change de date de départ, son ménage prévu suit tout seul (trigger en base) ; un ménage déjà commencé ou fait n'est pas touché, il est signalé.",
      reserve:
        "Le test lit la migration, pas la base : l'apply en prod se vérifie sur pg_trigger.",
    },
  },
  {
    cle: "menage_runs_creation",
    automate: {
      preuves: [
        { fichier: "lib/menage-runs.js", fonction: "createMenageRunsForDate" },
        { fichier: "lib/menage-runs.js", fonction: "ensureMenageRunsWindow" },
      ],
      declencheurs: [
        // Vercel seul depuis le 02/10 : le `schedule` de `menage-runs.yml`
        // est retiré (workflow_dispatch conservé pour les rattrapages).
        // Lot L1a (30/10, P-3.37) : sorti de beds24-sync vers api/cron/menage.js.
        {
          type: "vercel",
          chemin: "/api/cron/menage?job=runs",
          rythmes: ["0 17 * * *", "0 18 * * *"],
        },
      ],
      phrase:
        "Les ménages de demain se créent tout seuls la veille (17 h et 18 h UTC) — mais personne n'y est assigné automatiquement.",
    },
  },
  {
    cle: "menage_cloture_fallback",
    automate: {
      preuves: [
        {
          fichier: "lib/menage-day-closure.js",
          fonction: "runDayClosureFallback",
        },
      ],
      declencheurs: [
        // Vercel seul depuis le 02/10 (mêmes heures UTC, sans créneau) : le
        // `schedule` de `menage-day-closure.yml` est retiré (workflow_dispatch
        // conservé pour les rattrapages). Lot L1a (30/10, P-3.37) : sorti de
        // beds24-sync vers api/cron/menage.js.
        {
          type: "vercel",
          chemin: "/api/cron/menage?job=fin-de-journee",
          rythmes: ["0 19 * * *", "0 20 * * *"],
        },
      ],
      phrase:
        "Une journée de ménage non clôturée par l'agent se clôture toute seule à 21 h (heure de Paris, été comme hiver), et l'admin en est averti.",
    },
  },
  {
    // ⚠️ EN SOMMEIL DEPUIS LE 30/10/2026 — Beds24 en sommeil (lot L2,
    // P-3.37). Même forme que `annonces_nouvelles` : un `null` documenté,
    // pour que l'opérateur PROPOSE au lieu de s'effacer devant un automate
    // qui ne tourne plus. Rien n'est supprimé : le code est en place.
    cle: "sync_beds24_lecture",
    automate: null,
    raison:
      "Beds24 est en sommeil depuis le 30/10 (décision Dimitri, lot L2 de P-3.37 ; interrupteur unique lib/beds24-sommeil.js) : plus aucun appel à Beds24, et la tâche planifiée est retirée de vercel.json. Le miroir des réservations Beds24 ne se relit plus : une réservation prise sur une plateforme n'arrive plus dans l'appli par Beds24. L'opérateur doit le dire, pas promettre une relecture. Au réveil : reprendre l'entrée d'avant (historique git du lot L2).",
  },
  {
    // ⚠️ EN SOMMEIL DEPUIS LE 30/10/2026 — Beds24 en sommeil (lot L2,
    // P-3.37). Même forme que `annonces_nouvelles` : un `null` documenté,
    // pour que l'opérateur PROPOSE au lieu de s'effacer devant un automate
    // qui ne tourne plus. Rien n'est supprimé : le code est en place.
    cle: "prix_lecture_beds24",
    automate: null,
    raison:
      "Beds24 est en sommeil depuis le 30/10 (décision Dimitri, lot L2 de P-3.37 ; interrupteur unique lib/beds24-sommeil.js) : plus aucun appel à Beds24, et la tâche planifiée est retirée de vercel.json. La lecture horaire des prix et disponibilités Beds24 est sautée (pricing-sync ne garde que la santé PriceLabs). L'opérateur doit le dire, pas promettre une relecture. Au réveil : reprendre l'entrée d'avant (historique git du lot L2).",
  },
];

const _parCle = new Map(AUTOMATES.map((e) => [e.cle, e]));

/** L'entrée d'un automate, ou `undefined` si la clé n'est pas dans la liste fermée. */
function automate(cle) {
  return _parCle.get(cle);
}

/**
 * La phrase que l'opérateur peut réutiliser pour s'effacer — `null` si
 * l'automate n'existe pas OU si la clé est inconnue : dans les deux cas,
 * l'opérateur n'a PAS le droit de s'effacer.
 */
function phraseAutomate(cle) {
  const e = _parCle.get(cle);
  return e && e.automate ? e.automate.phrase : null;
}

/** Les clés dont l'automate est déclaré absent (avec sa raison). */
function automatesAbsents() {
  return AUTOMATES.filter((e) => e.automate === null).map((e) => ({
    cle: e.cle,
    raison: e.raison,
  }));
}

module.exports = { AUTOMATES, automate, phraseAutomate, automatesAbsents };
